import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";

import type { NurseryRole } from "@/contexts/NurseryContext";
import type {
  PublishAcknowledgementItemDtoScopeRole,
  RequestEventRecordAcknowledgementDtoReminderFrequency,
} from "@/generated/model";
// The canonical scope enum, owned by the shared acknowledgement context. Orval
// emits a structurally identical copy per DTO, so the request-shaped one is
// cast from this at the point the payload is built — one cast, at the boundary,
// rather than a second draft type.
import { PublishAcknowledgementItemDtoScopeType as ScopeType } from "@/generated/model";
import { describeScope, type ScopeDraft } from "@/lib/acknowledgements/scope";
import type { NurseryMemberOption } from "@/lib/member-display";
import { nurseryRoleLabel } from "@/lib/roles";

import { AcknowledgementRecipientPicker } from "./AcknowledgementRecipientPicker";

interface AcknowledgementScopePickerProps {
  draft: ScopeDraft;
  onChange: (draft: ScopeDraft) => void;
  members: (NurseryMemberOption & { role?: string })[];
  isMembersLoading: boolean;
  disabled: boolean;
  alreadyRequestedUserIds: string[];
  dueAt: string;
  onDueAtChange: (value: string) => void;
  reminderFrequency: RequestEventRecordAcknowledgementDtoReminderFrequency;
  onReminderFrequencyChange: (
    value: RequestEventRecordAcknowledgementDtoReminderFrequency,
  ) => void;
}

/**
 * Who has to acknowledge, plus the deadline and reminder cadence (AC6/AC32).
 *
 * The named-individuals list is the existing recipient picker, unchanged — its
 * row structure is load-bearing and must not be refactored: the handler sits on
 * the `div role="button"` and the Checkbox carries none, because nesting an
 * interactive control inside a button is invalid HTML and, inside a form, the
 * hidden input re-dispatches a bubbling click that loops until the tab locks.
 *
 * "A specific room or team" is deliberately absent from the options. Staff are
 * not linked to rooms anywhere in the data model, so the option would resolve to
 * nobody — it needs a staff-to-room relation first.
 */
export function AcknowledgementScopePicker({
  draft,
  onChange,
  members,
  isMembersLoading,
  disabled,
  alreadyRequestedUserIds,
  dueAt,
  onDueAtChange,
  reminderFrequency,
  onReminderFrequencyChange,
}: AcknowledgementScopePickerProps) {
  /**
   * Which roles can actually be offered.
   *
   * Derived from the members who can be asked rather than hardcoded, so the
   * dropdown never offers a role that resolves to nobody — an incident can only
   * be sent to people who can open it.
   */
  const offerableRoles = [
    ...new Set(
      members
        .map((member) => member.role)
        .filter((role): role is string => Boolean(role)),
    ),
  ];

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-caption-bold font-caption-bold text-default-font">
          Who has to acknowledge this?
        </span>
        <Select
          className="w-full"
          value={draft.scopeType}
          onValueChange={(value) =>
            // Switching mode keeps the other fields, so a half-made choice is
            // not lost — the payload is narrowed at submit time instead.
            onChange({ ...draft, scopeType: value as ScopeType })
          }
          disabled={disabled}
        >
          <Select.Item value={ScopeType.NAMED_INDIVIDUALS}>
            Specific people
          </Select.Item>
          <Select.Item value={ScopeType.ROLE}>Everyone with a role</Select.Item>
          <Select.Item value={ScopeType.ALL_STAFF}>
            Everyone who can be asked
          </Select.Item>
        </Select>
        <span className="text-caption font-caption text-subtext-color">
          {describeScope(draft, members.length)}
        </span>
      </div>

      {draft.scopeType === ScopeType.ROLE && (
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-caption-bold font-caption-bold text-default-font">
            Role
          </span>
          <Select
            className="w-full"
            placeholder="Choose a role"
            value={draft.scopeRole ?? undefined}
            onValueChange={(value) =>
              onChange({
                ...draft,
                scopeRole: value as PublishAcknowledgementItemDtoScopeRole,
              })
            }
            disabled={disabled}
          >
            {offerableRoles.map((role) => (
              <Select.Item key={role} value={role}>
                {nurseryRoleLabel(role as NurseryRole) || role}
              </Select.Item>
            ))}
          </Select>
        </div>
      )}

      {draft.scopeType === ScopeType.NAMED_INDIVIDUALS && (
        <AcknowledgementRecipientPicker
          members={members}
          selectedUserIds={draft.staffUserIds}
          onToggle={(userId) =>
            onChange({
              ...draft,
              staffUserIds: draft.staffUserIds.includes(userId)
                ? draft.staffUserIds.filter((id) => id !== userId)
                : [...draft.staffUserIds, userId],
            })
          }
          isLoading={isMembersLoading}
          disabled={disabled}
          alreadyRequestedUserIds={alreadyRequestedUserIds}
        />
      )}

      <div className="flex w-full flex-col gap-4 sm:flex-row">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-caption-bold font-caption-bold text-default-font">
            Due by (optional)
          </span>
          <TextField className="w-full" disabled={disabled}>
            <TextField.Input
              type="date"
              value={dueAt}
              onChange={(event) => onDueAtChange(event.target.value)}
              data-testid="ack-scope-due-at"
            />
          </TextField>
        </div>

        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-caption-bold font-caption-bold text-default-font">
            Remind
          </span>
          <Select
            className="w-full"
            value={reminderFrequency}
            onValueChange={(value) =>
              onReminderFrequencyChange(
                value as RequestEventRecordAcknowledgementDtoReminderFrequency,
              )
            }
            disabled={disabled}
          >
            <Select.Item value="NONE">Don&apos;t remind</Select.Item>
            <Select.Item value="DAILY">Daily</Select.Item>
            <Select.Item value="WEEKLY">Weekly</Select.Item>
          </Select>
          <span className="text-caption font-caption text-subtext-color">
            Shows when a chase is due. Nothing is emailed.
          </span>
        </div>
      </div>
    </div>
  );
}
